import moment from 'moment-timezone';
import { getEventsUpcoming } from '../../../../../ui-kitchen/src/api/api';
import { transformEventPreview } from '../../../../../ui-kitchen/src/transformers';
import { API_EVENT_ID, EVENT_TYPES } from './constants';
import { fetchEventDetails, updateResyEventItem } from '../AmexExperiential/ExperientialEvents';
import { ALL_DAY, ISO_DATE_FORMAT } from '../../../../../ui-kitchen/src/constants/resyCore';

const DEFAULT_SELECTED_PARTY_SIZE = 2;
const DEFAULT_CITY_CODE = 'ny';
const DATE_FORMAT = 'YYYY-MM-DD';

const timezoneMapping = {
  EST5EDT: 'America/New_York',
};

const getCurrentMomentDate = () => {
  moment.tz.setDefault('America/New_York');
  const nowEST = moment();
  const currentDate = nowEST.format('DD-MM-YYYY');
  return moment(currentDate, 'DD-MM-YYYY');
};

const getCurrentMomentMonth = () => {
  moment.tz.setDefault('America/New_York');
  const nowEST = moment();
  const currentMonthMoment = nowEST.month();
  return currentMonthMoment;
};

const getCurrentMomentYear = () => {
  moment.tz.setDefault('America/New_York');
  const nowEST = moment();
  const currentYearMoment = nowEST.year();
  return currentYearMoment;
};

const sortFeaturedEventsByDate = (featuredEvents) => (featuredEvents.sort((event1, event2) => (moment(event1.date, DATE_FORMAT)
  .diff(moment(event2.date, DATE_FORMAT)))));

export const getFeaturedEvents = (events, previewSearchText) => {
  let isFeaturedWithPreviewExists = true;
  let featuredEvents = events
    ?.filter((eventItem) => !eventItem?.sold_out && eventItem?.preview?.toLowerCase().startsWith(previewSearchText?.toLowerCase()));

  if (featuredEvents?.length === 0) {
    featuredEvents = events
      ?.filter((eventItem) => !eventItem?.sold_out);
    isFeaturedWithPreviewExists = false;
  }

  const sortedEarliestFeaturedEvent = sortFeaturedEventsByDate(featuredEvents)[0];
  if (sortedEarliestFeaturedEvent) {
    let featuredPreviewText = '';
    if (isFeaturedWithPreviewExists) {
      featuredPreviewText = sortedEarliestFeaturedEvent?.preview.slice(previewSearchText?.length, sortedEarliestFeaturedEvent?.preview?.length).trim() || '';
    } else {
      featuredPreviewText = sortedEarliestFeaturedEvent?.preview;
    }
    return { ...sortedEarliestFeaturedEvent, featuredPreviewText };
  } return null;
};

const loadJson = (event) => ({
  url_slug: event.eventSlug,
  date: event.date,
  name: event.title,
  id: event.eventId,
  is_global_dining_access: event.isGda,
  image_preview: event.photo,
  responsive_images: event.responsiveImages,
  time: event.time,
  event_type_id: EVENT_TYPES.API,
  preview: event.preview,
});

export const fetchEvents = async (venueId, isSoldOutUpcomingEnabled, venue) => {
  const upcomingEvents = await getEventsUpcoming({ venue_id: venueId });
  const rawEvents = upcomingEvents?.events.map((event) => {
    const tranformedEvent = transformEventPreview(event);
    return { ...tranformedEvent, preview: event.preview };
  });

  if (!isSoldOutUpcomingEnabled) {
    const events = rawEvents?.map((event) => ((loadJson(event))));
    return events;
  }

  const eventsFinalDetails = await Promise.all(rawEvents.map(async (event) => {
    const initialEventData = loadJson(event);
    const eventsDataItem = {
      event_slug: event.eventSlug,
      location: venue?.location.urlSlug,
      venue_slug: venue.urlSlug,
    };

    const eventDetails = await fetchEventDetails(eventsDataItem, DEFAULT_SELECTED_PARTY_SIZE);

    if (!eventDetails) return initialEventData;

    const selectedDate = moment().format(ISO_DATE_FORMAT);
    const selectedCoreTime = ALL_DAY;

    const updatedEventDetials = updateResyEventItem(eventsDataItem,
      eventDetails, DEFAULT_CITY_CODE, timezoneMapping, DEFAULT_SELECTED_PARTY_SIZE, selectedDate, selectedCoreTime, undefined, undefined);

    return {
      ...initialEventData,
      show_notify: updatedEventDetials?.showNotify,
      sold_out: updatedEventDetials?.sold_out,
      notify: updatedEventDetials?.notify,
    };
  }));

  return eventsFinalDetails?.filter((event) => !!event);
};

const getMonthsLabel = (eventMonth) => moment().month(eventMonth).format('MMMM');

const upComingAPIEventsWithDate = (upcomingAPIEvents) => {
  const upcomingMonthsEventsUpdated = upcomingAPIEvents?.map((event) => {
    const eventDate = moment(event.date, DATE_FORMAT);
    const eventMonth = eventDate.month();
    const eventMonthName = getMonthsLabel(eventMonth);

    const eventYear = new Date(event.date).getFullYear();
    const padZero = (num) => (num < 10 ? '0' : '') + num;
    const eventUpdated = {
      ...event,
      id: API_EVENT_ID,
      event_type_id: EVENT_TYPES.API,
      monthId: `${eventMonth}`,
      yearId: `${eventYear}`,
      yearMonthId: `${eventYear}${padZero(eventMonth + 1)}`,
      monthYearDisplay: `${eventMonthName} ${eventYear}`,
    };

    return eventUpdated;
  });
  return upcomingMonthsEventsUpdated;
};

export const groupUpcomingEventsBasedOnMonth = (upcomingAPIEvents) => {
  const apiEvents = upComingAPIEventsWithDate(upcomingAPIEvents?.filter((event) => !!event));

  const finalUpcomingEvents = apiEvents?.reduce((r, a) => {
    r[a.yearMonthId] = r[a.yearMonthId] || [];
    r[a.yearMonthId].push(a);
    return r;
  }, Object.create(null));

  const orderedFinalUpcomingEvents = Object.keys(finalUpcomingEvents).sort().reduce(
    (obj, key) => {
      obj[key] = finalUpcomingEvents[key].sort((a, b) => (a.id - b.id));
      return obj;
    },
    {}
  );

  return orderedFinalUpcomingEvents;
};

export const currentRecurringEvents = (recurringJSONEvents) => {
  const currentMonthsRecurrEvents = recurringJSONEvents.filter((event) => {
    const parseCurrentDate = getCurrentMomentDate();
    const currentMonthMoment = getCurrentMomentMonth();
    const { responsive_images: images } = event;
    const hasImage = images && Object.prototype.hasOwnProperty.call((images), '16:9') && images['16:9'];
    let isPresent = false;
    if (event.event_type_id === EVENT_TYPES.WALK_IN) {
      const parsedEndDate = moment(event.end_date, DATE_FORMAT);
      const parsedStartDate = moment(event.start_date, DATE_FORMAT);
      const parseStartDateMonth = parsedStartDate.month();
      const parseStartDateYear = parsedStartDate.year();

      /*
        1.The start month should be less or equal to current month in the same year
          or start date's year is lesser than current year
        2.Current date should be same or before end date
      */
      if (((parseStartDateMonth <= currentMonthMoment
        && parseStartDateYear === getCurrentMomentYear())
        || parseStartDateYear < getCurrentMomentYear())
        && parseCurrentDate.isSameOrAfter(parsedStartDate)
        && parseCurrentDate.isSameOrBefore(parsedEndDate)
      ) {
        isPresent = true;
      }
    }
    if (isPresent && hasImage) {
      return event;
    }
    return null;
  });

  const sortedCurrentMonthsRecurrEvents = currentMonthsRecurrEvents?.sort((a, b) => (a.id - b.id));
  return sortedCurrentMonthsRecurrEvents;
};

export const getRecurringEventImage = (images, useCustomImage) => {
  const {
    '16:9': image16x9,
    custom: imageCustom,
  } = images;

  let result = image16x9;
  if (useCustomImage && imageCustom) {
    result = imageCustom;
  }
  return result;
};

export const fetchPastEvents = async (pastEventsContent = {}, venue) => {
  const {
    location: {
      urlSlug: locationSlug,
    },
    urlSlug,
  } = venue;

  const { past_events: pastEvents } = pastEventsContent;
  if (!locationSlug || !urlSlug || !pastEvents || pastEvents?.length === 0) return null;
  const eventsFinalDetails = await Promise.all(pastEvents?.map(
    async (event) => {
      if (!event?.event_slug || !event?.order_id) return null;
      const eventItem = {
        event_slug: event?.event_slug,
        location: locationSlug,
        venue_slug: urlSlug,
      };

      const eventData = await fetchEventDetails(eventItem, DEFAULT_SELECTED_PARTY_SIZE);
      if (eventData) {
        return {
          ...eventData,
          order_id: event?.order_id,
        };
      }
      return null;
    }
  ));
  return eventsFinalDetails?.filter((item) => item !== null).sort((a, b) => (a.order_id - b.order_id));
};
